// components/RegistroManual.jsx
// CORREGIDO: Agregado selector de canal para ventas

import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp, doc, updateDoc, increment, query, getDocs, where } from 'firebase/firestore';
import { auditarOperacion } from '../logic/logicEngine';
import { useEstrellaHueso } from '../hooks/useEstrellaHueso';
import { useTranslation } from '../hooks/useTranslation';
import AutocompleteInput from './AutocompleteInput';
import { formatMoneyUniversal } from '../util/formatMoneyUniversal';

// ✅ CANALES DE VENTA DISPONIBLES
const CANALES_VENTA = {
  MERCADO_LIBRE: { id: 'MERCADO_LIBRE', nombre: 'Mercado Libre', comision: 27.1, emoji: '🛒' },
  TIENDA_FISICA: { id: 'TIENDA_FISICA', nombre: 'Tienda Física', comision: 0, emoji: '🏪' },
  SHOPIFY: { id: 'SHOPIFY', nombre: 'Shopify', comision: 3.5, emoji: '🛍️' },
  INSTAGRAM: { id: 'INSTAGRAM', nombre: 'Instagram', comision: 5, emoji: '📱' },
  WHATSAPP: { id: 'WHATSAPP', nombre: 'WhatsApp Business', comision: 0, emoji: '💬' },
  PROPIA: { id: 'PROPIA', nombre: 'Plataforma Propia', comision: 0, emoji: '🌐' },
  CUSTOM: { id: 'CUSTOM', nombre: 'Personalizado', comision: null, emoji: '⚙️' }
};

const RegistroManual = ({ usuarioActual, idioma, saldoActual = 0, guardarProductoEnCatalogo, onSuccess, onError }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    monto: '',
    cantidad: '1',
    concepto: '',
    tipo: 'gasto',
    fuentePago: 'negocio',
    fecha: new Date().toLocaleDateString('en-CA'),
    tercero: '',
    tipoPago: 'contado',
    fechaLimitePago: '',
    fechaVencimientoProducto: '',
    // ✅ NUEVO: Canal de venta
    canalVenta: 'MERCADO_LIBRE',
    comisionPersonalizada: '',
    // Nuevos campos para devoluciones
    productoDevuelto: '',
    cantidadDevuelta: '1',
    accionDevolucion: 'reembolso',
    productoNuevo: '',
    cantidadNuevo: '1',
    motivoDevolucion: ''
  });
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [productos, setProductos] = useState([]);
  const [infoProductoSeleccionado, setInfoProductoSeleccionado] = useState(null);
  const [productosCriticos, setProductosCriticos] = useState([]);
  const [productosEstrella, setProductosEstrella] = useState([]);
  const db = getFirestore();
  const { alertaCompraHueso } = useEstrellaHueso();

  // Formatear moneda según idioma
  const formatMoney = (valor, lang) => {
    const pais = lang === 'en' ? 'US' : 'CO';
    return formatMoneyUniversal(valor, pais);
  };

  // ============================================================
  // FUNCIÓN PARA CALCULAR PRECIO SUGERIDO DE LIQUIDACIÓN
  // ============================================================
  const calcularPrecioLiquidacion = (costoUnitario, diasEnStock) => {
    if (!costoUnitario || costoUnitario <= 0) return null;
    
    let precio = null;
    let estrategia = '';
    let urgencia = '';
    
    if (diasEnStock >= 180) {
      precio = costoUnitario * 0.8;
      estrategia = t('liquidacionPerdida') || '💀 PÉRDIDA CONTROLADA';
      urgencia = t('urgencia180') || '⚠️ URGENTE: más de 180 días';
    } else if (diasEnStock >= 90) {
      precio = costoUnitario * 0.9;
      estrategia = t('recuperarCapital') || '💰 RECUPERAR CAPITAL';
      urgencia = t('urgencia90') || '⚠️ Alerta: más de 90 días';
    } else if (diasEnStock >= 60) {
      precio = costoUnitario * 1.0;
      estrategia = t('alCosto') || '📦 AL COSTO';
      urgencia = t('urgencia60') || '⚡ Recupera inversión';
    } else if (diasEnStock >= 30) {
      precio = costoUnitario * 1.1;
      estrategia = t('promocionLigera') || '🔥 PROMOCIÓN LIGERA';
      urgencia = t('urgencia30') || '💡 Libera flujo de caja';
    } else {
      return null;
    }
    
    return {
      precio: Math.round(precio),
      estrategia,
      urgencia
    };
  };

  // ============================================================
  // FUNCIÓN PARA OBTENER INFORMACIÓN DE PRODUCTO DESDE INVENTARIO
  // ============================================================
  const obtenerInfoProducto = async (nombreProducto) => {
    if (!usuarioActual?.uid || !nombreProducto) return null;
    
    try {
      const inventarioRef = collection(db, 'inventario');
      const q = query(inventarioRef, 
        where('userId', '==', usuarioActual.uid),
        where('producto', '==', nombreProducto)
      );
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) return null;
      
      const producto = snapshot.docs[0].data();
      const clasificacion = producto.clasificacion || 'NEUTRO';
      const diasEnStock = producto.diasEnStock || 0;
      const margenNeto = producto.margenNetoReal || 0;
      const costoUnitario = producto.costoUnitario || 0;
      
      let precioLiquidacion = null;
      if (clasificacion === 'HUESO') {
        precioLiquidacion = calcularPrecioLiquidacion(costoUnitario, diasEnStock);
      }
      
      return {
        nombre: nombreProducto,
        clasificacion,
        diasEnStock,
        margenNeto,
        costoUnitario,
        precioLiquidacion,
        existe: true
      };
    } catch (error) {
      console.error('Error obteniendo info producto:', error);
      return null;
    }
  };

  // ============================================================
  // FUNCIÓN PARA CARGAR PRODUCTOS CRÍTICOS Y ESTRELLA
  // ============================================================
  const cargarProductosAuditoria = async () => {
    if (!usuarioActual?.uid) return;
    
    try {
      const inventarioRef = collection(db, 'inventario');
      const q = query(inventarioRef, where('userId', '==', usuarioActual.uid));
      const snapshot = await getDocs(q);
      
      const criticos = [];
      const estrellas = [];
      
      snapshot.forEach(doc => {
        const producto = doc.data();
        const clasificacion = producto.clasificacion || 'NEUTRO';
        const cantidad = producto.cantidad || 0;
        
        if (clasificacion === 'HUESO' && cantidad > 0) {
          criticos.push({
            nombre: producto.producto,
            cantidad: producto.cantidad,
            diasEnStock: producto.diasEnStock || 0,
            costoUnitario: producto.costoUnitario || 0
          });
        } else if (clasificacion === 'ESTRELLA' && cantidad > 0) {
          estrellas.push({
            nombre: producto.producto,
            margen: producto.margenNetoReal || 0
          });
        }
      });
      
      criticos.sort((a, b) => b.diasEnStock - a.diasEnStock);
      setProductosCriticos(criticos.slice(0, 5));
      setProductosEstrella(estrellas.slice(0, 5));
    } catch (error) {
      console.error('Error cargando productos auditoría:', error);
    }
  };

  // ============================================================
  // Cargar productos desde INVENTARIO (NO desde products)
  // ============================================================
  useEffect(() => {
    const cargarProductos = async () => {
      if (!usuarioActual?.uid) return;
      try {
        const q = query(collection(db, 'inventario'), where('userId', '==', usuarioActual.uid));
        const snapshot = await getDocs(q);
        const lista = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          lista.push({ 
            id: doc.id, 
            nombre: data.producto,
            ...data 
          });
        });
        setProductos(lista);
        console.log('📦 Productos cargados desde inventario:', lista.length);
        
        await cargarProductosAuditoria();
      } catch (error) {
        console.error('Error cargando productos:', error);
      }
    };
    cargarProductos();
  }, [usuarioActual?.uid, db]);

  const getCategoria = (tipo) => {
    switch(tipo) {
      case 'gasto': return 'GASTO_NO_OPERACIONAL';
      case 'ingreso': return 'INGRESO';
      case 'compra': return 'INVENTARIO';
      case 'venta': return 'VENTA';
      case 'devolucion_venta': return 'DEVOLUCION_VENTA';
      case 'devolucion_compra': return 'DEVOLUCION_COMPRA';
      default: return 'OTROS';
    }
  };

  const getTipoFlujo = (tipo) => {
    switch(tipo) {
      case 'gasto':
      case 'compra':
        return 'egreso';
      case 'ingreso':
      case 'venta':
        return 'ingreso';
      case 'devolucion_venta':
        return 'egreso';
      case 'devolucion_compra':
        return 'ingreso';
      default: return 'egreso';
    }
  };

  const limpiarFormulario = () => {
    setFormData({
      monto: '',
      cantidad: '1',
      concepto: '',
      tipo: 'gasto',
      fuentePago: 'negocio',
      fecha: new Date().toLocaleDateString('en-CA'),
      tercero: '',
      tipoPago: 'contado',
      fechaLimitePago: '',
      fechaVencimientoProducto: '',
      canalVenta: 'MERCADO_LIBRE',
      comisionPersonalizada: '',
      productoDevuelto: '',
      cantidadDevuelta: '1',
      accionDevolucion: 'reembolso',
      productoNuevo: '',
      cantidadNuevo: '1',
      motivoDevolucion: ''
    });
    setInfoProductoSeleccionado(null);
  };

  // Verificar límite de transacciones según plan
  const verificarLimiteTransacciones = async () => {
    if (!usuarioActual?.uid) return true;
    
    const plan = usuarioActual.plan || 'starter';
    
    // Business y Elite no tienen límite
    if (plan === 'business' || plan === 'elite') return true;
    
    // Calcular inicio del mes actual
    const inicioMes = new Date();
    inicioMes.setDate(1);
    inicioMes.setHours(0, 0, 0, 0);
    
    const q = query(
      collection(db, 'registros'),
      where('userId', '==', usuarioActual.uid),
      where('fecha', '>=', inicioMes)
    );
    const snapshot = await getDocs(q);
    const transaccionesMes = snapshot.size;
    
    // Pro: 1000 transacciones/mes, Starter: 100 transacciones/mes
    const limite = plan === 'pro' ? 1000 : 100;
    
    if (transaccionesMes >= limite) {
      const mensaje = idioma === 'en'
        ? `❌ You have reached the limit of ${limite} transactions for this month. Upgrade to Business for unlimited transactions.`
        : `❌ Has alcanzado el límite de ${limite} transacciones de este mes. Actualiza a Business para transacciones ilimitadas.`;
      setValidationError(mensaje);
      return false;
    }
    
    return true;
  };

  // ✅ OBTENER COMISIÓN DEL CANAL SELECCIONADO
  const getComisionCanal = () => {
    if (formData.canalVenta === 'CUSTOM') {
      return parseFloat(formData.comisionPersonalizada) || 0;
    }
    const canal = CANALES_VENTA[formData.canalVenta];
    return canal ? canal.comision : 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationError(null);
    setSuccessMessage(null);

    try {
      const montoNum = parseFloat(formData.monto);
      const cantidadNum = parseInt(formData.cantidad);
      
      // ============================================================
// 👇 PROCESAR DEVOLUCIONES - CORREGIDO
// ============================================================
if (formData.tipo === 'devolucion_venta' || formData.tipo === 'devolucion_compra') {
  const cantidadDevuelta = parseInt(formData.cantidadDevuelta) || 1;
  const accion = formData.accionDevolucion || 'reembolso';
  
  // Buscar el producto en inventario
  const inventarioRef = collection(db, 'inventario');
  const qProducto = query(inventarioRef, 
    where('producto', '==', formData.productoDevuelto),
    where('userId', '==', usuarioActual.uid)
  );
  const snapshotProducto = await getDocs(qProducto);
  
  if (snapshotProducto.empty) {
    setValidationError(t('productoNoExiste') || '❌ El producto no existe en inventario');
    setLoading(false);
    return;
  }
  
  const productoData = snapshotProducto.docs[0].data();
  const inventarioDocId = snapshotProducto.docs[0].id;
  const precioUnitario = productoData.costoUnitario || (montoNum / cantidadDevuelta);
  
  // ✅ CALCULAR EL AJUSTE DE STOCK SEGÚN EL TIPO DE DEVOLUCIÓN
  const ajusteStock = formData.tipo === 'devolucion_venta' 
    ? cantidadDevuelta        // Devolución de venta: SUMA (+)
    : -cantidadDevuelta;      // Devolución de compra: RESTA (-)
  
  // ✅ VALIDAR QUE NO QUEDE STOCK NEGATIVO EN DEVOLUCIÓN DE COMPRA
  if (formData.tipo === 'devolucion_compra') {
    const stockActual = productoData.cantidad || 0;
    if (stockActual < cantidadDevuelta) {
      setValidationError(`❌ No puedes devolver ${cantidadDevuelta} unidades. Stock actual: ${stockActual}`);
      setLoading(false);
      return;
    }
  }
  
  // CASO A: Reembolso en dinero
  if (accion === 'reembolso') {
    // ✅ ACTUALIZAR INVENTARIO CON EL AJUSTE CORRECTO
    await updateDoc(doc(db, 'inventario', inventarioDocId), {
      cantidad: increment(ajusteStock)
    });
    
    // ✅ AJUSTE DE SALDO (YA ESTÁ CORRECTO)
    const ajusteSaldo = formData.tipo === 'devolucion_venta' 
      ? -(precioUnitario * cantidadDevuelta)  // Devolución venta: RESTAS del saldo (reembolsas)
      : (precioUnitario * cantidadDevuelta);   // Devolución compra: SUMAS al saldo (te reembolsan)
    
    await updateDoc(doc(db, 'usuarios', usuarioActual.uid), {
      saldoCaja: increment(ajusteSaldo)
    });
  } 
  else if (accion === 'cambio') {
    const productoNuevoNombre = formData.productoNuevo;
    const cantidadNuevo = parseInt(formData.cantidadNuevo) || cantidadDevuelta;
    
    if (!productoNuevoNombre) {
      setValidationError(t('productoNuevoRequerido') || '❌ Debes especificar el producto nuevo para el cambio');
      setLoading(false);
      return;
    }
    
    const qNuevo = query(inventarioRef,
      where('producto', '==', productoNuevoNombre),
      where('userId', '==', usuarioActual.uid)
    );
    const snapshotNuevo = await getDocs(qNuevo);
    
    if (snapshotNuevo.empty) {
      setValidationError(t('productoNuevoNoExiste') || `❌ El producto "${productoNuevoNombre}" no existe en inventario`);
      setLoading(false);
      return;
    }
    
    const productoNuevoData = snapshotNuevo.docs[0].data();
    const inventarioNuevoId = snapshotNuevo.docs[0].id;
    
    if (productoNuevoData.cantidad < cantidadNuevo) {
      setValidationError(t('stockInsuficienteNuevo') || `❌ Stock insuficiente de ${productoNuevoNombre}`);
      setLoading(false);
      return;
    }
    
    // ✅ ACTUALIZAR PRODUCTO ORIGINAL CON EL AJUSTE CORRECTO
    await updateDoc(doc(db, 'inventario', inventarioDocId), {
      cantidad: increment(ajusteStock)
    });
    
    // ✅ ACTUALIZAR PRODUCTO NUEVO (RESTAR porque SALE del inventario)
    await updateDoc(doc(db, 'inventario', inventarioNuevoId), {
      cantidad: increment(-cantidadNuevo)
    });
  }
  else if (accion === 'perdida') {
    // ✅ Solo registrar, NO se devuelve al inventario
    // PERO: Si es devolución_compra, el producto SALE del inventario (pérdida)
    // Si es devolución_venta, el producto NO entra (se perdió)
    if (formData.tipo === 'devolucion_compra') {
      // Validar que haya stock suficiente
      const stockActual = productoData.cantidad || 0;
      if (stockActual < cantidadDevuelta) {
        setValidationError(`❌ No puedes dar de baja ${cantidadDevuelta} unidades. Stock actual: ${stockActual}`);
        setLoading(false);
        return;
      }
      
      // RESTAR del inventario (pérdida)
      await updateDoc(doc(db, 'inventario', inventarioDocId), {
        cantidad: increment(-cantidadDevuelta)
      });
    }
    // Si es devolución_venta con pérdida, NO se hace nada con el stock
  }
  
  const registroDevolucion = {
    texto: `${formData.tipo === 'devolucion_venta' ? 'Devolución en Venta' : 'Devolución en Compra'}: ${cantidadDevuelta}x ${formData.productoDevuelto} - Acción: ${accion}${formData.productoNuevo ? ` → ${formData.productoNuevo}` : ''}${formData.motivoDevolucion ? ` (Motivo: ${formData.motivoDevolucion})` : ''}`,
    concepto: `Devolución: ${formData.productoDevuelto}`,
    valor: accion === 'reembolso' ? precioUnitario * cantidadDevuelta : 0,
    tipo: getTipoFlujo(formData.tipo),
    categoria: getCategoria(formData.tipo),
    emoji: formData.tipo === 'devolucion_venta' ? '⬅️' : '➡️',
    cantidad: cantidadDevuelta,
    fecha: new Date(formData.fecha + 'T12:00:00'),
    serverTimestamp: serverTimestamp(),
    userId: usuarioActual.uid,
    metodo: 'manual',
    fechaRegistro: formData.fecha,
    tipoOperacion: formData.tipo,
    accionDevolucion: accion,
    productoDevuelto: formData.productoDevuelto,
    productoNuevo: formData.productoNuevo || null,
    cantidadNuevo: formData.cantidadNuevo || null,
    motivoDevolucion: formData.motivoDevolucion || null,
    tercero: formData.tercero || null,
    // ✅ GUARDAR EL AJUSTE PARA FUTURAS ELIMINACIONES
    ajusteStockAplicado: ajusteStock
  };
  
  await addDoc(collection(db, 'registros'), registroDevolucion);
  
  setSuccessMessage(t('devolucionExitosa') || `✅ Devolución registrada: ${cantidadDevuelta}x ${formData.productoDevuelto}`);
  limpiarFormulario();
  await cargarProductosAuditoria();
  if (onSuccess) onSuccess();
  setLoading(false);
  return;
}
// ============================================================
// FIN PROCESAMIENTO DEVOLUCIONES
// ============================================================
      
      if (formData.tipo === 'compra' && formData.concepto) {
        const productoParaAlerta = {
          nombre: formData.concepto,
          clasificacion: 'HUESO',
          diasEnStock: 0
        };
        alertaCompraHueso(productoParaAlerta);
      }
      
      if (isNaN(montoNum) || montoNum <= 0) {
        setValidationError(t('montoInvalido') || '❌ El monto debe ser un número mayor a cero');
        setLoading(false);
        return;
      }
        
      const tipoFlujo = getTipoFlujo(formData.tipo);
      const categoria = getCategoria(formData.tipo);
      const costoUnitario = montoNum / cantidadNum;
      
      // ✅ LIMITAR PRODUCTOS EN INVENTARIO (STARTER) - SOLO PARA COMPRAS
      if (formData.tipo === 'compra') {
        const plan = usuarioActual?.plan || 'starter';
        if (plan === 'starter') {
          const inventarioRef = collection(db, 'inventario');
          const q = query(inventarioRef, where('userId', '==', usuarioActual.uid));
          const snapshot = await getDocs(q);
          const cantidadProductos = snapshot.size;
          
          const productoQuery = query(inventarioRef, 
            where('producto', '==', formData.concepto), 
            where('userId', '==', usuarioActual.uid)
          );
          const productoSnapshot = await getDocs(productoQuery);
          const productoExiste = !productoSnapshot.empty;
          
          if (!productoExiste && cantidadProductos >= 20) {
            setValidationError('❌ Has alcanzado el límite de 20 productos en inventario. Actualiza a Pro o Business.');
            setLoading(false);
            return;
          }
        }
      }
      
      const tipoTexto = {
        gasto: t('gasto') || 'Gasto', 
        ingreso: t('ingreso') || 'Ingreso', 
        compra: t('compraInventario') || 'Compra', 
        venta: t('venta') || 'Venta'
      };
      
      // ✅ OBTENER COMISIÓN DEL CANAL
      const comisionPorcentaje = getComisionCanal();
      const canalInfo = CANALES_VENTA[formData.canalVenta] || CANALES_VENTA.MERCADO_LIBRE;
      
      let textoCompleto = `${tipoTexto[formData.tipo]}: ${cantidadNum}x ${formData.concepto} por ${montoNum}`;
      
      // ✅ AGREGAR CANAL AL TEXTO SI ES VENTA
      if (formData.tipo === 'venta') {
        const comisionStr = comisionPorcentaje > 0 ? ` (${comisionPorcentaje}% comisión)` : '';
        textoCompleto += ` - Canal: ${canalInfo.nombre}${comisionStr}`;
      }
      
      if (formData.tercero) {
        textoCompleto += ` - ${formData.tipo === 'compra' ? 'Proveedor' : 'Cliente'}: ${formData.tercero}`;
      }
      
      if (formData.tipoPago === 'credito' && formData.fechaLimitePago) {
        textoCompleto += ` (Crédito, vence: ${formData.fechaLimitePago})`;
      }
      
      const auditoria = auditarOperacion(
        textoCompleto,
        montoNum,
        { saldoCaja: saldoActual },
        idioma
      );

      if (!auditoria.validado) {
        setValidationError(auditoria.mensajeValidacion);
        setLoading(false);
        return;
      }

      const esAportePersonal = (formData.fuentePago === 'personal' && 
                          (formData.tipo === 'compra' || formData.tipo === 'gasto'));
      const esGastoNegocio = (tipoFlujo === 'egreso' && formData.fuentePago === 'negocio');

      if (esGastoNegocio && saldoActual < montoNum) {
        setValidationError(t('saldoInsuficiente', { saldo: formatMoney(saldoActual, idioma), monto: formatMoney(montoNum, idioma) }));
        setLoading(false);
        return;
      }

      if (formData.tipo === 'venta') {
        const inventarioRef = collection(db, 'inventario');
        const qInventario = query(inventarioRef, where('producto', '==', formData.concepto), where('userId', '==', usuarioActual.uid));
        const snapshotInventario = await getDocs(qInventario);
        
        let stockActual = 0;
        if (!snapshotInventario.empty) {
          stockActual = snapshotInventario.docs[0].data().cantidad;
        }
        
        if (stockActual < cantidadNum) {
          setValidationError(t('stockInsuficiente', { stock: stockActual, solicitado: cantidadNum }));
          setLoading(false);
          return;
        }
      }

      if (formData.tipo === 'compra' && guardarProductoEnCatalogo) {
        await guardarProductoEnCatalogo(formData.concepto, usuarioActual.uid);
      }

      const userRef = doc(db, 'usuarios', usuarioActual.uid);
      
      if (esAportePersonal) {
        await updateDoc(userRef, {
          deudaConDueño: increment(montoNum),
          aportesPersonales: increment(montoNum)
        });
        setValidationError(t('advertenciaPersonal'));
        setTimeout(() => setValidationError(null), 5000);
      } else if (esGastoNegocio) {
        await updateDoc(userRef, {
          saldoCaja: increment(-montoNum)
        });
      }

      // ✅ REGISTRO CON CANAL DE VENTA
      const registroData = {
        texto: textoCompleto,
        concepto: formData.concepto,
        valor: montoNum,
        tipo: tipoFlujo,
        categoria: categoria,
        emoji: formData.tipo === 'compra' ? '📦' : formData.tipo === 'venta' ? '💰' : auditoria.emoji,
        recomendacion: esAportePersonal 
          ? t('aportePersonalRecomendacion') || '💰 Aporte de capital personal. El negocio te debe este dinero.'
          : auditoria.recomendacion,
        cantidad: cantidadNum,
        costoUnitario: costoUnitario,
        fecha: new Date(formData.fecha + 'T12:00:00'),
        serverTimestamp: serverTimestamp(),
        userId: usuarioActual?.uid,
        metodo: 'manual',
        fechaRegistro: formData.fecha,
        tipoOperacion: formData.tipo,
        fuentePago: formData.fuentePago,
        esAportePersonal: esAportePersonal,
        tercero: formData.tercero || null,
        tipoPago: formData.tipoPago,
        fechaLimitePago: formData.tipoPago === 'credito' ? formData.fechaLimitePago : null,
        fechaVencimientoProducto: (formData.tipo === 'compra' && formData.fechaVencimientoProducto) ? formData.fechaVencimientoProducto : null,
        // ✅ NUEVOS CAMPOS: Canal de venta
        canalVenta: formData.tipo === 'venta' ? canalInfo.nombre : null,
        comisionPorcentaje: formData.tipo === 'venta' ? comisionPorcentaje : null,
        canalVentaId: formData.tipo === 'venta' ? formData.canalVenta : null
      };
      
      await addDoc(collection(db, 'registros'), registroData);

      // ✅ ACTUALIZAR INVENTARIO CON CANAL DE VENTA
      if (formData.tipo === 'compra' || formData.tipo === 'venta') {
        const inventarioRef = collection(db, 'inventario');
        const qInv = query(inventarioRef, where('producto', '==', formData.concepto), where('userId', '==', usuarioActual.uid));
        const snapshotInv = await getDocs(qInv);
        
        if (snapshotInv.empty) {
          const inventarioData = {
            producto: formData.concepto,
            cantidad: formData.tipo === 'compra' ? cantidadNum : -cantidadNum,
            costoUnitario: costoUnitario,
            costoTotal: montoNum,
            userId: usuarioActual.uid,
            fechaActualizacion: serverTimestamp(),
            // ✅ Si es venta, guardar el canal
            ...(formData.tipo === 'venta' && {
              ultimoCanalVenta: canalInfo.nombre,
              ultimaComisionVenta: comisionPorcentaje
            })
          };
          
          if (formData.tipo === 'compra' && formData.fechaVencimientoProducto) {
            inventarioData.fechaVencimiento = formData.fechaVencimientoProducto;
            inventarioData.alertaVencimientoEnviada = false;
          }
          
          await addDoc(inventarioRef, inventarioData);
        } else {
          const inventarioDoc = snapshotInv.docs[0];
          const dataActual = inventarioDoc.data();
          let nuevaCantidad;
          let nuevoCostoTotal;
          let nuevoCostoUnitario;
          
          if (formData.tipo === 'compra') {
            nuevaCantidad = dataActual.cantidad + cantidadNum;
            nuevoCostoTotal = (dataActual.cantidad * dataActual.costoUnitario) + montoNum;
            nuevoCostoUnitario = nuevoCostoTotal / nuevaCantidad;
          } else {
            nuevaCantidad = dataActual.cantidad - cantidadNum;
            nuevoCostoTotal = dataActual.costoTotal - montoNum;
            nuevoCostoUnitario = dataActual.costoUnitario;
          }
          
          const updateData = {
            cantidad: nuevaCantidad,
            costoTotal: nuevoCostoTotal,
            costoUnitario: nuevoCostoUnitario,
            fechaActualizacion: serverTimestamp(),
            // ✅ Si es venta, guardar el canal
            ...(formData.tipo === 'venta' && {
              ultimoCanalVenta: canalInfo.nombre,
              ultimaComisionVenta: comisionPorcentaje
            })
          };
          
          if (formData.tipo === 'compra' && formData.fechaVencimientoProducto) {
            updateData.fechaVencimiento = formData.fechaVencimientoProducto;
            updateData.alertaVencimientoEnviada = false;
          }
          
          await updateDoc(doc(db, 'inventario', inventarioDoc.id), updateData);
        }
      }

      setSuccessMessage(t('exito') || '✅ Movimiento registrado exitosamente');
      limpiarFormulario();
      await cargarProductosAuditoria();
      
      if (onSuccess) onSuccess();

    } catch (error) {
      console.error('Error guardando registro:', error);
      setValidationError(`${t('errorGeneral') || 'Error'}: ${error.message}`);
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RENDERIZADO
  // ============================================================
  return (
    <div className="bg-[#1e293b] rounded-2xl p-6 mb-8 border border-blue-900/30">
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <span>✏️</span> 
        {t('tituloRegistroManual') || 'Registro Manual de Movimientos'}
      </h3>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-900/30 border border-green-500/30 rounded-lg text-green-400 text-sm">
          {successMessage}
        </div>
      )}

      {validationError && !successMessage && (
        <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
          {validationError}
        </div>
      )}

      {/* Productos Críticos y Estrella */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
          <h4 className="text-red-400 text-sm font-bold mb-2">{t('critical_products_title')}</h4>
          {productosCriticos.length === 0 ? (
            <p className="text-gray-500 text-xs">{t('no_critical_products')}</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {productosCriticos.map((p, idx) => {
                const precioLiq = calcularPrecioLiquidacion(p.costoUnitario, p.diasEnStock);
                return (
                  <div key={idx} className="border-b border-red-500/20 pb-2">
                    <p className="text-white text-sm font-medium">📉 {p.nombre}</p>
                    <p className="text-gray-400 text-xs">📦 {p.cantidad} und | ⏱️ {p.diasEnStock} {t('diasSinRotacion') || 'días sin rotación'}</p>
                    {precioLiq && (
                      <p className="text-cyan-400 text-xs">{t('precioSugerido') || '💰 Sugerido'}: {formatMoney(precioLiq.precio, idioma)} ({precioLiq.estrategia})</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
          <h4 className="text-green-400 text-sm font-bold mb-2">{t('productosEstrella') || '⭐ Productos Estrella'}</h4>
          {productosEstrella.length === 0 ? (
            <p className="text-gray-500 text-xs">{t('sinProductosEstrella') || '⚠️ Aún no hay productos ESTRELLA'}</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {productosEstrella.map((p, idx) => (
                <div key={idx} className="border-b border-green-500/20 pb-2">
                  <p className="text-white text-sm font-medium">{p.nombre}</p>
                  <p className="text-green-400 text-xs">⭐ {t('margen') || 'Margen'}: {p.margen}%</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Información del producto seleccionado */}
      {infoProductoSeleccionado && infoProductoSeleccionado.existe && (
        <div className="mb-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
          <h4 className="text-blue-400 text-sm font-bold mb-2">{t('infoProducto') || '📊 Información del Producto'}</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <p className="text-gray-400">{t('clasificacion') || 'Clasificación'}:</p>
            <p className={`font-bold ${
              infoProductoSeleccionado.clasificacion === 'ESTRELLA' ? 'text-green-400' :
              infoProductoSeleccionado.clasificacion === 'HUESO' ? 'text-red-400' : 'text-yellow-400'
            }`}>
              {infoProductoSeleccionado.clasificacion}
            </p>
            <p className="text-gray-400">{t('diasSinVentas') || 'Días sin ventas'}:</p>
            <p className="text-white">{infoProductoSeleccionado.diasEnStock} días</p>
            {infoProductoSeleccionado.margenNeto > 0 && (
              <>
                <p className="text-gray-400">{t('margen') || 'Margen'}:</p>
                <p className="text-white">{infoProductoSeleccionado.margenNeto}%</p>
              </>
            )}
            {infoProductoSeleccionado.precioLiquidacion && (
              <>
                <p className="text-gray-400">{t('precioLiquidacion') || 'Precio sugerido para liquidar'}:</p>
                <p className="text-cyan-400">{formatMoney(infoProductoSeleccionado.precioLiquidacion.precio, idioma)}</p>
                <p className="text-gray-400">{t('estrategia') || 'Estrategia'}:</p>
                <p className="text-yellow-400">{infoProductoSeleccionado.precioLiquidacion.estrategia}</p>
              </>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-1">{t('tipoMovimiento') || 'Tipo de movimiento'}</label>
            <select
              name="tipo"
              value={formData.tipo}
              onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
              className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              required
            >
              <option value="gasto">{t('gasto') || 'Gasto'}</option>
              <option value="ingreso">{t('ingreso') || 'Ingreso'}</option>
              <option value="compra">{t('compraInventario') || 'Compra (inventario)'}</option>
              <option value="venta">{t('venta') || 'Venta'}</option>
              <option value="devolucion_venta">⬅️ {t('devolucionVenta') || 'Devolución en Venta'}</option>
              <option value="devolucion_compra">➡️ {t('devolucionCompra') || 'Devolución en Compra'}</option>
            </select>
          </div>

          {(formData.tipo === 'gasto' || formData.tipo === 'compra') && (
            <div>
              <label className="block text-gray-400 text-sm mb-1">{t('fuentePago') || 'Pagado con...'}</label>
              <select
                name="fuentePago"
                value={formData.fuentePago}
                onChange={(e) => setFormData(prev => ({ ...prev, fuentePago: e.target.value }))}
                className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="negocio">{t('businessFunds') || '💰 Fondos del negocio'}</option>
                <option value="personal">{t('personalFunds') || '👤 Fondos personales (Inyección de capital)'}</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-gray-400 text-sm mb-1">{t('cantidad') || 'Cantidad (unidades)'}</label>
            <input
              type="number"
              name="cantidad"
              value={formData.cantidad}
              onChange={(e) => setFormData(prev => ({ ...prev, cantidad: e.target.value }))}
              placeholder="1"
              className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              required
              step="1"
              min="1"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-1">{t('montoTotal') || 'Monto Total'}</label>
            <input
              type="number"
              name="monto"
              value={formData.monto}
              onChange={(e) => setFormData(prev => ({ ...prev, monto: e.target.value }))}
              placeholder="0.00"
              className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              required
              step="any"
              min="0.01"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-1">{t('fecha') || 'Fecha'}</label>
            <input
              type="date"
              name="fecha"
              value={formData.fecha}
              onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
              max={new Date().toLocaleDateString('en-CA')}
              className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-1">{t('tercero') || 'Proveedor / Cliente'}</label>
            <input
              type="text"
              name="tercero"
              value={formData.tercero}
              onChange={(e) => setFormData(prev => ({ ...prev, tercero: e.target.value }))}
              placeholder={formData.tipo === 'compra' ? 'Ej: Distribuidora XYZ' : 'Ej: Juan Pérez'}
              className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          {(formData.tipo === 'compra' || formData.tipo === 'venta') && (
            <div>
              <label className="block text-gray-400 text-sm mb-1">{t('tipoPago') || 'Tipo de pago'}</label>
              <select
                name="tipoPago"
                value={formData.tipoPago}
                onChange={(e) => setFormData(prev => ({ ...prev, tipoPago: e.target.value, fechaLimitePago: '' }))}
                className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="contado">{t('contado') || '💰 Contado'}</option>
                <option value="credito">{t('credito') || '📅 Crédito'}</option>
              </select>
            </div>
          )}

          {(formData.tipo === 'compra' || formData.tipo === 'venta') && formData.tipoPago === 'credito' && (
            <div>
              <label className="block text-gray-400 text-sm mb-1">{t('fechaLimitePago') || 'Fecha límite de pago'}</label>
              <input
                type="date"
                name="fechaLimitePago"
                value={formData.fechaLimitePago}
                onChange={(e) => setFormData(prev => ({ ...prev, fechaLimitePago: e.target.value }))}
                min={new Date().toLocaleDateString('en-CA')}
                className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          )}

          {/* ✅ NUEVO: Selector de Canal de Venta - SOLO para ventas */}
          {formData.tipo === 'venta' && (
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-1">
                {t('canalVenta') || '📢 Canal de Venta'}
              </label>
              <select
                name="canalVenta"
                value={formData.canalVenta}
                onChange={(e) => setFormData(prev => ({ ...prev, canalVenta: e.target.value, comisionPersonalizada: '' }))}
                className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="MERCADO_LIBRE">🛒 Mercado Libre (27.1% comisión)</option>
                <option value="TIENDA_FISICA">🏪 Tienda Física (0% comisión)</option>
                <option value="SHOPIFY">🛍️ Shopify (3.5% comisión)</option>
                <option value="INSTAGRAM">📱 Instagram (5% comisión)</option>
                <option value="WHATSAPP">💬 WhatsApp Business (0% comisión)</option>
                <option value="PROPIA">🌐 Plataforma Propia (0% comisión)</option>
                <option value="CUSTOM">⚙️ Personalizado</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t('canalVentaInfo') || '💡 El canal de venta se usa para calcular la comisión y el margen real por canal.'}
              </p>
            </div>
          )}

          {/* ✅ NUEVO: Comisión personalizada - SOLO si se selecciona "Personalizado" */}
          {formData.tipo === 'venta' && formData.canalVenta === 'CUSTOM' && (
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-1">
                {t('comisionPersonalizada') || '⚙️ Comisión Personalizada (%)'}
              </label>
              <input
                type="number"
                value={formData.comisionPersonalizada}
                onChange={(e) => setFormData(prev => ({ ...prev, comisionPersonalizada: e.target.value }))}
                placeholder={t('comisionPlaceholder') || 'Ej: 15 (para 15% de comisión)'}
                className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          )}

          {/* Autocomplete para producto */}
          <div className="md:col-span-2">
            <label className="block text-gray-400 text-sm mb-1">{t('producto') || 'Producto'}</label>
            <AutocompleteInput
              value={formData.concepto}
              onChange={(val) => {
                setFormData(prev => ({ ...prev, concepto: val }));
                setValidationError(null);
                setSuccessMessage(null);
                setInfoProductoSeleccionado(null);
              }}
              options={productos.map(p => p.nombre).filter(Boolean)}
              placeholder={t('ejemploConcepto') || 'Escribe el nombre del producto...'}
              className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              onSelect={async (selected) => {
                const productoSeleccionado = productos.find(p => p.nombre === selected);
                if (productoSeleccionado) {
                  const info = await obtenerInfoProducto(productoSeleccionado.nombre);
                  setInfoProductoSeleccionado(info);
                }
              }}
            />
          </div>

          {formData.tipo === 'compra' && (
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-1">{t('fechaVencimientoProducto') || 'Fecha de vencimiento del producto'}</label>
              <input
                type="date"
                name="fechaVencimientoProducto"
                value={formData.fechaVencimientoProducto}
                onChange={(e) => setFormData(prev => ({ ...prev, fechaVencimientoProducto: e.target.value }))}
                min={new Date().toLocaleDateString('en-CA')}
                className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('dejarBlanco') || 'Dejar en blanco si no aplica'}
              </p>
            </div>
          )}

          {/* 👇 CAMPOS PARA DEVOLUCIONES */}
          {(formData.tipo === 'devolucion_venta' || formData.tipo === 'devolucion_compra') && (
            <div className="md:col-span-2 border-t border-blue-900/30 pt-4 mt-2">
              <h4 className="text-cyan-400 text-sm font-bold mb-3">
                {t('datosDevolucion') || '📋 Datos de la devolución'}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">
                    {t('productoDevuelto') || 'Producto devuelto *'}
                  </label>
                  <AutocompleteInput
                    value={formData.productoDevuelto || ''}
                    onChange={(val) => setFormData(prev => ({ ...prev, productoDevuelto: val }))}
                    options={productos.map(p => p.nombre).filter(Boolean)}
                    placeholder={t('nombreProductoDevuelto') || 'Ej: Pantalón de Hombre'}
                    className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-1">
                    {t('cantidadDevuelta') || 'Cantidad devuelta *'}
                  </label>
                  <input
                    type="number"
                    value={formData.cantidadDevuelta || '1'}
                    onChange={(e) => setFormData(prev => ({ ...prev, cantidadDevuelta: e.target.value }))}
                    min="1"
                    step="1"
                    className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-1">
                    {t('accionDevolucion') || 'Acción a tomar *'}
                  </label>
                  <select
                    value={formData.accionDevolucion || 'reembolso'}
                    onChange={(e) => setFormData(prev => ({ ...prev, accionDevolucion: e.target.value }))}
                    className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="reembolso">💰 {t('reembolsoDinero') || 'Reembolso en dinero'}</option>
                    <option value="cambio">🔄 {t('cambioProducto') || 'Cambio por otro producto'}</option>
                    <option value="perdida">📉 {t('perdidaInventario') || 'Pérdida (no entra a inventario)'}</option>
                  </select>
                </div>
                
                {formData.accionDevolucion === 'cambio' && (
                  <>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">
                        {t('productoNuevo') || 'Producto nuevo para cambio'}
                      </label>
                      <AutocompleteInput
                        value={formData.productoNuevo || ''}
                        onChange={(val) => setFormData(prev => ({ ...prev, productoNuevo: val }))}
                        options={productos.map(p => p.nombre).filter(Boolean)}
                        placeholder={t('nombreProductoNuevo') || 'Ej: Camisa Manga Larga'}
                        className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">
                        {t('cantidadNuevo') || 'Cantidad nuevo producto'}
                      </label>
                      <input
                        type="number"
                        value={formData.cantidadNuevo || '1'}
                        onChange={(e) => setFormData(prev => ({ ...prev, cantidadNuevo: e.target.value }))}
                        min="1"
                        step="1"
                        className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white"
                      />
                    </div>
                  </>
                )}
                
                <div className="md:col-span-2">
                  <label className="block text-gray-400 text-sm mb-1">
                    {t('motivoDevolucion') || 'Motivo de la devolución'}
                  </label>
                  <textarea
                    value={formData.motivoDevolucion || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, motivoDevolucion: e.target.value }))}
                    rows="2"
                    placeholder={t('motivoPlaceholder') || 'Ej: Producto defectuoso, talla incorrecta, etc.'}
                    className="w-full bg-[#0f172a] border border-blue-900/20 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 disabled:opacity-50"
        >
          {loading ? (t('guardando') || 'Guardando...') : (t('guardar') || 'Registrar Movimiento')}
        </button>
      </form>
    </div>
  );
};

export default RegistroManual;

